import useTodaysEvents from '$tablet-app/src/app/calendar/combined/useTodaysEvents';
import { calendarItemIsAfter } from '$tablet-app/src/app/calendar/common/actions';
import Checklist from '$tablet-app/src/app/components/Checklist/Checklist';
import { EmojiWithBackground } from '$tablet-app/src/app/components/EmojiWithBackground/EmojiWithBackground';
import { ProfileIcons } from '$tablet-app/src/app/components/ProfileIcons/ProfileIcons';
import Row from '$tablet-app/src/app/components/Row';
import Text from '$tablet-app/src/app/components/Text';
import { layout, margin, palette } from '$tablet-app/src/app/theme';
import useCurrentTime from '$tablet-app/src/app/utils/useCurrentTime';
import { useUpdateCalendarItem } from '@akin-home/calendar/combined';
import { CheckListItem } from '@akin-home/checklists';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useNavigation } from '@react-navigation/core';
import { format } from 'date-fns';
import React, { FC, useMemo } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';
import { CalendarItemUnion } from '../../calendar/types';
import { setChecklistItem } from '../../models';
import DashboardPanel from '../DashboardPanel';
import DashboardPanelTitle from '../DashboardPanelTitle';

const minute = 1000 * 60;

const EventDetails: FC = () => {
  const { items } = useTodaysEvents();

  const time = useCurrentTime({ interval: minute });
  const { navigate } = useNavigation();
  const updateCalendarItem = useUpdateCalendarItem();

  const item = useMemo(
    () => filterNextEvent(items, new Date(time)),
    [time, items]
  );

  const onUpdateChecklistItem = ({
    checklist,
  }: {
    checklist: CheckListItem;
    item: any;
  }) => {
    if (!item) return;
    updateCalendarItem(setChecklistItem(item, checklist));
  };

  const goToCalendar = () => navigate('Calendar');

  return (
    <DashboardPanel style={layout.flex_1}>
      {item ? (
        <Row height="100%">
          <LeftColumn
            showBorder={
              (item.notes && item.notes.length > 0) ||
              ('checklist' in item && item.checklist.length > 0)
            }
          >
            <DashboardPanelTitle text="UP NEXT" />

            <ScrollView>
              <TouchableOpacity onPress={goToCalendar}>
                <View style={margin(0, 0, 10)}>
                  {item.emoji ? (
                    <EmojiWithBackground emoji={item.emoji} />
                  ) : null}
                  <Text preset="h2">{item.title}</Text>
                  <Text>{format(item.start, 'EEEE, MMMM, d - hh:mm a')}</Text>
                </View>

                {item.participants?.length ? (
                  <View>
                    <Text bold>
                      {item.participants.length} participant
                      {item.participants.length > 1 ? 's' : ''}
                    </Text>
                    <Row paddingLeft={20} marginTop={10}>
                      <ProfileIcons
                        profiles={item.participants}
                        preset="overlapRight"
                      />
                    </Row>
                  </View>
                ) : null}
              </TouchableOpacity>
            </ScrollView>
          </LeftColumn>

          <RightColumn>
            {item.notes ? (
              <Row marginBottom={10}>
                <View style={margin(4, 0, 0)}>
                  <FontAwesomeIcon icon="sticky-note" />
                </View>

                <View style={[layout.flex_1, margin(0, 0, 0, 10)]}>
                  <Text bold>Notes</Text>
                  <Text>{item.notes}</Text>
                </View>
              </Row>
            ) : null}

            {'checklist' in item && item.checklist?.length ? (
              <Row>
                <View style={margin(4, 0, 0)}>
                  <FontAwesomeIcon icon="tasks" />
                </View>

                <View style={[layout.flex_1, margin(0, 0, 0, 10)]}>
                  <Checklist
                    checklist={item.checklist}
                    onChecklistPress={onUpdateChecklistItem}
                  />
                </View>
              </Row>
            ) : null}
          </RightColumn>
        </Row>
      ) : (
        <Row height="100%">
          <NoEventsPlaceholderPress onPress={goToCalendar}>
            <Text variant="h3">
              No upcoming events for today. Click here to add some.
            </Text>
          </NoEventsPlaceholderPress>
        </Row>
      )}
    </DashboardPanel>
  );
};

const LeftColumn = styled.View<{ showBorder: boolean }>`
  padding: 16px;
  border-right-width: ${({ showBorder }) => (showBorder ? 1 : 0)}px;
  border-right-color: ${palette.Outline_blue};
  flex: 1;
  height: 100%;
`;

const RightColumn = styled.ScrollView`
  padding: 16px;
  flex: 1;
  height: 100%;
`;

const NoEventsPlaceholderPress = styled.TouchableOpacity`
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const filterNextEvent = (events: CalendarItemUnion[], date: Date) =>
  events.find((e) => calendarItemIsAfter(date)(e));

export default EventDetails;
